import React from "react";
import Skeleton from "react-loading-skeleton";
import { CheckCircle, XCircle, Clock, DollarSign } from "lucide-react";

const TableAccountAdjustments = ({ data, isLoading }) => {
  function formatMoney(number, decPlaces, decSep, thouSep) {
    decPlaces = isNaN((decPlaces = Math.abs(decPlaces))) ? 2 : decPlaces;
    decSep = typeof decSep === "undefined" ? "." : decSep;
    thouSep = typeof thouSep === "undefined" ? "," : thouSep;
    var sign = number < 0 ? "-" : "";
    var i = String(
      parseInt((number = Math.abs(Number(number) || 0).toFixed(decPlaces)))
    );
    var j = (j = i.length) > 3 ? j % 3 : 0;

    return (
      sign +
      (j ? i.substr(0, j) + thouSep : "") +
      i.substr(j).replace(/(\decSep{3})(?=\decSep)/g, "$1" + thouSep) +
      (decPlaces
        ? decSep +
          Math.abs(number - i)
            .toFixed(decPlaces)
            .slice(2)
        : "")
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 2: // APPLIED
        return <CheckCircle size={16} className="text-green-500" />;
      case 3: // REFUNDED
      case 4: // RETURNED
      case 5: // CANCELED
      case 6: // FAILED
        return <XCircle size={16} className="text-red-500" />;
      case 7: // PROCESSING
      case 1: // PENDING
      case 0: // SCHEDULED
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Scheduled";
      case 1:
        return "Pending";
      case 2:
        return "Applied";
      case 3:
        return "Refunded";
      case 4:
        return "Returned";
      case 5:
        return "Canceled";
      case 6:
        return "Failed";
      case 7:
        return "Processing";
      default:
        return "Unknown";
    }
  };

  const getStatusColorClass = (status) => {
    switch (status) {
      case 2: // APPLIED
        return "text-green-600";
      case 3: // REFUNDED
      case 4: // RETURNED
      case 5: // CANCELED
      case 6: // FAILED
        return "text-red-600";
      case 7: // PROCESSING
      case 1: // PENDING
      case 0: // SCHEDULED
      default:
        return "text-yellow-600";
    }
  };

  return (
    <div className="w-full">
      <table className="w-full hidden sm:table">
        <thead>
          <tr className="border-b border-gray-600">
            <th className="text-left py-3 px-4 font-semibold text-gray-300 text-sm">
              Status
            </th>
            <th className="text-left py-3 px-4 font-semibold text-gray-300 text-sm">
              Date
            </th>
            <th className="text-left py-3 px-4 font-semibold text-gray-300 text-sm">
              Description
            </th>
            <th className="text-right py-3 px-4 font-semibold text-gray-300 text-sm">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <tr
                key={`skel-${index}`}
                className={`border-b border-gray-700 ${index % 2 === 0 ? "" : "bg-gray-800/30"}`}
              >
                <td className="py-3 px-4">
                  <Skeleton
                    height={16}
                    width={90}
                    baseColor="#374151"
                    highlightColor="#4B5563"
                  />
                </td>
                <td className="py-3 px-4">
                  <Skeleton
                    height={16}
                    width={100}
                    baseColor="#374151"
                    highlightColor="#4B5563"
                  />
                </td>
                <td className="py-3 px-4">
                  <Skeleton
                    height={16}
                    width={180}
                    baseColor="#374151"
                    highlightColor="#4B5563"
                  />
                </td>
                <td className="py-3 px-4 text-right">
                  <Skeleton
                    height={18}
                    width={80}
                    baseColor="#374151"
                    highlightColor="#4B5563"
                  />
                </td>
              </tr>
            ))}
          {!isLoading &&
            data?.map((row, index) => (
              <tr
                key={row.id}
                className={`border-b border-gray-700 hover:bg-gray-700/50 transition-colors ${
                  index % 2 === 0 ? "" : "bg-gray-800/30"
                }`}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(row.status)}
                    <span
                      className={`text-sm font-medium ${getStatusColorClass(row.status)}`}
                    >
                      {getStatusText(row.status)}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-gray-200 text-sm font-medium">
                    {new Date(row.created).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-gray-300 text-sm">
                    {row.note || row.payment_memo_code || "—"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <DollarSign size={16} className="text-gray-500" />
                    <span className="text-lg font-semibold text-gray-200">
                      {formatMoney(row.amount)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {/* Mobile stacked layout */}
      <div className="sm:hidden space-y-3">
        {isLoading &&
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`skel-m-${i}`}
              className="p-3 rounded-lg border border-gray-700 bg-gray-800/50"
            >
              <div className="flex items-center gap-2 mb-2">
                <Skeleton
                  height={16}
                  width={90}
                  baseColor="#374151"
                  highlightColor="#4B5563"
                />
              </div>
              <div className="mb-2">
                <Skeleton
                  height={14}
                  width={180}
                  baseColor="#374151"
                  highlightColor="#4B5563"
                />
              </div>
              <div className="flex justify-between text-sm">
                <Skeleton
                  height={14}
                  width={100}
                  baseColor="#374151"
                  highlightColor="#4B5563"
                />
                <Skeleton
                  height={14}
                  width={80}
                  baseColor="#374151"
                  highlightColor="#4B5563"
                />
              </div>
            </div>
          ))}
        {!isLoading &&
          data?.map((row) => (
            <div
              key={row.id}
              className="p-3 rounded-lg border border-gray-700 bg-gray-800/50"
            >
              <div className="flex items-center gap-2 mb-2">
                {getStatusIcon(row.status)}
                <span
                  className={`text-sm font-medium ${getStatusColorClass(row.status)}`}
                >
                  {getStatusText(row.status)}
                </span>
              </div>
              <div className="text-gray-300 text-sm mb-2">
                {row.note || row.payment_memo_code || "—"}
              </div>
              <div className="text-gray-400 text-sm">
                {new Date(row.created).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
              <div className="text-right mt-2">
                <div className="inline-flex items-center justify-end gap-1">
                  <DollarSign size={16} className="text-gray-500" />
                  <span className="text-base font-semibold text-gray-200">
                    {formatMoney(row.amount)}
                  </span>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default TableAccountAdjustments;
